import { AfterViewInit, Component, Inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { LoadingService } from "@coso/loading";
import { DialogComponent, DialogType, IS_SAAS, setTabsManualActivation } from "@coso/utility";
import { combineLatest, forkJoin, Observable } from "rxjs";
import { take } from "rxjs/operators";
import { GroupStorage, HighAvailabilityModes, IConfigurationHolder, MappingMode, UserMappingOperation } from "../../models/auto-generated";
import { AUserPermissionsService } from "../../services";
import { SettingsService } from "../../services/settings.service";
import { SettingsStore } from "../../stores/settings.store";
import { handleError } from "../../utils/handle-error";
import { SettingsValidator } from "../../utils/settings-validator";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";

@Component({
  standalone:false,
  templateUrl: "./settings.component.html",
  styleUrls: ["./settings.component.scss"]
})
export class SettingsComponent implements OnInit, AfterViewInit {
  @ViewChild("validationRef", { static: true }) validationRef: TemplateRef<any>;
  incompatibilities: string[];

  errorMessage: string;
  successMessage: string;
  warningMessage: string;
  instanceId: string;
  isDirty$: Observable<boolean>;
  isValid$: Observable<boolean>;
  isFailoverBackupInstance: boolean;
  isReadOnly: boolean;
  constructor(
    @Inject(IS_SAAS) public isSaaS: boolean,
    public userPermissionsService: AUserPermissionsService,
    public loadingService: LoadingService,
    public router: Router,
    private settingsService: SettingsService,
    private settingsStore: SettingsStore,
    private route: ActivatedRoute,
    private modalService: NgbModal
  ) {}

  ngAfterViewInit(): void {
    /** This will make tabs to be compliant with WAI ARIA practices */
    setTabsManualActivation();
    /** This will set the current route tab selected. */
    this.route.firstChild.url.subscribe((segments) => {
      const segment = segments[0].path;
      const tab = document.querySelector(`[id="tab-${segment}"]`);
      // Remove tabindex attribute
      tab.removeAttribute("tabindex");
      // Set the tab as selected
      tab.setAttribute("aria-selected", "true");
    });
  }

  ngOnInit(): void {
    this.instanceId = this.route.snapshot.paramMap.get("instance-id");
    this.isDirty$ = this.settingsStore.isDirty$;
    this.isValid$ = this.settingsStore.isValid$;
    this.loadSettings();
  }

  saveSettings() {
    this.successMessage = this.warningMessage = this.errorMessage = undefined;
    combineLatest([this.settingsStore.settings$.pipe(take(1)), this.settingsStore.hasGroupStorageChanged$.pipe(take(1))]).subscribe(([current, hasGroupStorageChanged]) => {
      if (hasGroupStorageChanged) {
        const validationResult = SettingsValidator.validateOnGroupStorageChange(current);
        if (validationResult.valid) {
          this.save(current, true);
        } else {
          this.incompatibilities = validationResult.incompatibilities;
          /** Prompt user about incompatibilities with the current settings. */
          const modalRef = this.modalService.open(DialogComponent, {
            keyboard: false,
            ariaLabelledBy: "modalTitle"
          });
          modalRef.componentInstance.options = {
            titleText: "Incompatible custom settings",
            type: DialogType.Warning,
            contentTemplateRef: this.validationRef,
            rightButtons: [
              {
                label: "No",
                type: "button",
                class: "btn btn-dark mr-2",
                onClick: () => {
                  this.errorMessage = `The operation was cancelled.`;
                  modalRef.dismiss();
                }
              },
              {
                label: "Yes",
                type: "button",
                class: "btn btn-primary",
                onClick: () => {
                  const settings: IConfigurationHolder = {
                    ...current,
                    includeMsTeamsDocumentLocations: current.includeMsTeamsDocumentLocations && current.groupStorage !== GroupStorage.SharePoint,
                    appendMsTeamsDefaultPermissions: current.includeMsTeamsDocumentLocations && current.appendMsTeamsDefaultPermissions && current.groupStorage !== GroupStorage.SharePoint,
                    sharePoint: {
                      ...current.sharePoint,
                      sharepointConfig: current.sharePoint.sharepointConfig.map((config) => {
                        return {
                          ...config,
                          userMapping: { ...config.userMapping, mode: MappingMode.APPEND, userPairs: config.userMapping.userPairs.filter((u) => u.operation !== UserMappingOperation.Block) },
                          permissionMapping: { ...config.permissionMapping, permissionPairs: [] },
                          specialPermissions: []
                        };
                      })
                    }
                  };
                  this.save(settings, true);
                  modalRef.dismiss();
                }
              }
            ],
            onClose: () => modalRef.dismiss()
          };
        }
      } else {
        this.save(current);
      }
    });
  }

  discardChanges() {
    this.errorMessage = this.successMessage = undefined;
    const modalRef = this.modalService.open(DialogComponent, {
      keyboard: false,
      ariaLabelledBy: "modalTitle"
    });
    modalRef.componentInstance.options = {
      titleText: "Confirm Discard Changes",
      type: DialogType.Warning,
      contentText: "Are you sure you want to discard all changes? This action cannot be reversed.",
      rightButtons: [
        { label: "No", type: "button", class: "btn btn-dark mr-2", onClick: () => modalRef.dismiss() },
        {
          label: "Yes",
          type: "button",
          class: "btn btn-primary",
          onClick: () => {
            this.successMessage = "All changes have been successfully discarded.";
            this.settingsStore.undoChanges();
            modalRef.dismiss();
          }
        }
      ],
      onClose: () => modalRef.dismiss()
    };
  }

  resetToDefault() {
    this.errorMessage = this.successMessage = undefined;
    const modalRef = this.modalService.open(DialogComponent, {
      keyboard: false,
      ariaLabelledBy: "modalTitle"
    });
    modalRef.componentInstance.options = {
      titleText: "Reset Settings to Default",
      type: DialogType.Warning,
      contentText: "Are you sure you want to restore settings to their original defaults? This action cannot be reversed and will only reset Event Priority settings.",
      rightButtons: [
        { label: "No", type: "button", class: "btn btn-dark mr-2", onClick: () => modalRef.dismiss() },
        {
          label: "Yes",
          type: "button",
          class: "btn btn-primary",
          onClick: () => {
            this.settingsStore.settings$.pipe(take(1)).subscribe((current) => {
              const settings: IConfigurationHolder = {
                ...current,
                eventPriorityConfiguration: []
              };
              this.save(settings);
              modalRef.dismiss();
            });
          }
        }
      ],
      onClose: () => modalRef.dismiss()
    };
  }

  private save(current: IConfigurationHolder, hasGroupStorageChanged?: boolean) {
    this.loadingService.start("Saving settings", "saveSettings");
    this.settingsService.save(this.instanceId, current).subscribe(
      (data) => {
        this.successMessage = "All changes have been successfully saved.";
        this.settingsStore.initializeSettings(data);
        if (hasGroupStorageChanged && data.groupStorage !== GroupStorage.SharePoint) {
          this.warningMessage = `Please make sure that you have configured a connection to ${
            current.groupStorage === GroupStorage.ActiveDirectory ? "Active Directory" : "Microsoft Entra ID"
          }. If not, you can configure one <a class="underline-link" href="/configuration/${this.instanceId}/connections/${
            current.groupStorage === GroupStorage.ActiveDirectory ? "active-directory" : "entra-id"
          }" target="_blank">here</a>.`;
        }
        this.loadingService.stop("saveSettings");
      },
      (error) => {
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
        this.loadingService.stop("saveSettings");
      }
    );
  }

  private loadSettings() {
    this.loadingService.start("Loading settings", "loadSettings");
    forkJoin([this.userPermissionsService.hasLimitedAccess$, this.userPermissionsService.isCustomerAgent$, this.settingsService.get(this.instanceId)]).subscribe(
      ([hasLimitedAccess, isCustomerAgent, settings]) => {
        this.isReadOnly = hasLimitedAccess || isCustomerAgent;
        this.isFailoverBackupInstance = settings.highAvailabilityMode === HighAvailabilityModes.FailoverBackup;
        this.settingsStore.setIsReadOnly(this.isReadOnly || this.isFailoverBackupInstance);
        this.settingsStore.initializeSettings(settings);
        this.loadingService.stop("loadSettings");
      },
      (error) => {
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
        this.loadingService.stop("loadSettings");
      }
    );
  }
}
